import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AdminStatCard } from "./AdminStatCard";
import { Search, RefreshCw, Car, CheckCircle, Key, Wrench, CalendarDays, Plus, Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminFlota = ({ cars, onAddCar, onEditCar, onDeleteCar, onRefresh }) => {
  const [stats, setStats] = useState({ total: 0, available: 0, rented: 0, maintenance: 0, reserved: 0 });
  const [search, setSearch] = useState("");

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(`${API}/admin/fleet/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(res.data);
    } catch (e) { console.error(e); }
  };

  const toAbsoluteUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `${BACKEND_URL}${url}`;
  };

  const filtered = cars.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return `${c.brand} ${c.model}`.toLowerCase().includes(s) || (c.fuel_type || "").toLowerCase().includes(s);
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">Flota e Automjeteve</h2>
        <Button onClick={onAddCar} className="bg-yellow-600 hover:bg-yellow-500" data-testid="add-car-btn">
          <Plus className="h-4 w-4 mr-2" /> Shto Automjet
        </Button>
      </div>

      <div className="bg-gradient-to-r from-zinc-800 to-zinc-900 rounded-xl p-5 mb-6 border border-yellow-600/20">
        <p className="text-yellow-500 text-xs font-semibold tracking-wider uppercase">Menaxho Flotën Tënde</p>
        <p className="text-white text-2xl font-bold mt-1">{stats.total} <span className="text-gray-400 text-base font-normal">automjete regjistruar</span></p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        <AdminStatCard value={stats.total} label="Gjithsej" color="yellow" icon={<Car className="h-5 w-5" />} />
        <AdminStatCard value={stats.available} label="Disponibël" color="green" icon={<CheckCircle className="h-5 w-5" />} />
        <AdminStatCard value={stats.rented} label="Me qira" color="blue" icon={<Key className="h-5 w-5" />} />
        <AdminStatCard value={stats.maintenance} label="Mirëmbajtje" color="orange" icon={<Wrench className="h-5 w-5" />} />
        <AdminStatCard value={stats.reserved} label="Rezervuar" color="purple" icon={<CalendarDays className="h-5 w-5" />} />
      </div>

      <div className="flex gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Kërko sipas markës, tabelës..." className="bg-zinc-800 border-gray-700 text-white pl-10" data-testid="fleet-search" />
        </div>
        <Button variant="outline" onClick={() => { onRefresh(); loadStats(); }} className="border-gray-700 text-gray-400">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <Car className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p>Nuk ka automjete të regjistruara</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((car) => (
            <div key={car.id} className="bg-zinc-900 border border-gray-800 rounded-xl overflow-hidden hover:border-gray-700 transition-colors cursor-pointer" onClick={() => onEditCar(car)} data-testid={`fleet-car-${car.id}`}>
              <div className="h-40 bg-zinc-800 flex items-center justify-center">
                {car.image_url ? (
                  <img src={toAbsoluteUrl(car.image_url)} alt={`${car.brand} ${car.model}`} className="w-full h-full object-contain bg-gray-700" />
                ) : (
                  <Car className="h-12 w-12 text-gray-600" />
                )}
              </div>
              <div className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-white font-semibold">{car.brand} {car.model}</p>
                    <p className="text-gray-500 text-sm">{car.year} | {car.fuel_type} | {car.transmission}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-yellow-500 font-bold">€{car.price_per_day}/ditë</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${car.available ? "bg-green-600/20 text-green-500" : "bg-red-600/20 text-red-500"}`}>
                      {car.available ? "Disponibël" : "Jo aktiv"}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2 mt-3 pt-3 border-t border-gray-800">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 border-yellow-600/40 text-yellow-500 hover:bg-yellow-600/10"
                    onClick={(e) => { e.stopPropagation(); onEditCar(car); }}
                    data-testid={`edit-car-${car.id}`}
                  >
                    Edito
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-600/40 text-red-500 hover:bg-red-600/10"
                    onClick={(e) => { e.stopPropagation(); onDeleteCar && onDeleteCar(car.id); }}
                    data-testid={`delete-car-${car.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
